import React, { useEffect, useState } from "react";
import { Table, Button, Form, Row, Col, Modal, Badge } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";

// Phase 4A, §5/§13/§22. Performer participation is entirely separate from
// official assignment (tasks.assigned_to) — adding/editing/removing a
// performer here never changes who the task's official owner is. Multiple
// employees may be added; contribution percentages are never required to
// total 100.
const PERFORMER_ROLES = ["PRIMARY_PERFORMER", "CO_PERFORMER", "SUPPORT", "REVIEW_SUPPORT", "TESTING_SUPPORT", "DOCUMENTATION_SUPPORT", "OTHER"];
const PERFORMER_STATUSES = ["ASSIGNED", "IN_PROGRESS", "COMPLETED", "REMOVED", "CANCELLED"];
const WORK_TYPES = ["DEVELOPMENT", "TESTING", "SUPPORT", "ANALYSIS", "DOCUMENTATION", "REVIEW", "DEPLOYMENT", "COORDINATION", "OTHER"];

const initialAddForm = { user_id: "", performer_role: "CO_PERFORMER", responsibility: "", contribution_percentage: "", estimated_effort_minutes: "" };
const initialWorkLogForm = { work_date: new Date().toISOString().slice(0, 10), work_description: "", effort_minutes: "", work_type: "DEVELOPMENT" };

export default function TaskPerformerManager({ taskId, readOnly }) {
  const [performers, setPerformers] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(initialAddForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [workLogTarget, setWorkLogTarget] = useState(null);
  const [workLogForm, setWorkLogForm] = useState(initialWorkLogForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [perfRes, logRes] = await Promise.all([
        api.get(`/tasks/${taskId}/performers`),
        api.get(`/tasks/${taskId}/work-logs`),
      ]);
      setPerformers(Array.isArray(perfRes.data?.data) ? perfRes.data.data : []);
      setWorkLogs(Array.isArray(logRes.data?.data) ? logRes.data.data : []);
    } catch (err) {
      console.error("Load performers error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/users").then((res) => setUsers(Array.isArray(res.data) ? res.data : [])).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const submitAdd = async () => {
    if (!addForm.user_id) { toast.warning("Select an employee"); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/performers`, {
        performers: [{
          user_id: Number(addForm.user_id),
          performer_role: addForm.performer_role,
          responsibility: addForm.responsibility || undefined,
          contribution_percentage: addForm.contribution_percentage !== "" ? Number(addForm.contribution_percentage) : undefined,
          estimated_effort_minutes: addForm.estimated_effort_minutes !== "" ? Number(addForm.estimated_effort_minutes) : undefined,
        }],
      });
      toast.success("Performer added");
      setShowAdd(false);
      setAddForm(initialAddForm);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_PERFORMER_EXISTS") toast.error("This employee is already an active performer on this task.");
      else if (data?.code === "TASK_PROJECT_MEMBER_REQUIRED") toast.error("This employee must be an active member of this task's project/module.");
      else toast.error(data?.message || "Failed to add performer");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      performer_role: p.performer_role, responsibility: p.responsibility || "",
      contribution_percentage: p.contribution_percentage ?? "", estimated_effort_minutes: p.estimated_effort_minutes ?? "",
      actual_effort_minutes: p.actual_effort_minutes ?? "",
    });
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/tasks/${taskId}/performers/${editing.id}`, {
        performer_role: editForm.performer_role,
        responsibility: editForm.responsibility || null,
        contribution_percentage: editForm.contribution_percentage !== "" ? Number(editForm.contribution_percentage) : null,
        estimated_effort_minutes: editForm.estimated_effort_minutes !== "" ? Number(editForm.estimated_effort_minutes) : null,
        actual_effort_minutes: editForm.actual_effort_minutes !== "" ? Number(editForm.actual_effort_minutes) : null,
      });
      toast.success("Performer updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update performer");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (performer, status) => {
    try {
      await api.patch(`/tasks/${taskId}/performers/${performer.id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const removePerformer = async (performer) => {
    const reason = window.prompt(`Remove ${performer.user_name} as a performer? Enter a reason (optional):`);
    if (reason === null) return;
    try {
      await api.delete(`/tasks/${taskId}/performers/${performer.id}`, { data: { removal_reason: reason || undefined } });
      toast.success("Performer removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove performer");
    }
  };

  const submitWorkLog = async () => {
    if (!workLogForm.effort_minutes) { toast.warning("Effort (minutes) is required"); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/work-logs`, {
        user_id: workLogTarget.user_id,
        work_date: workLogForm.work_date,
        work_description: workLogForm.work_description || undefined,
        effort_minutes: Number(workLogForm.effort_minutes),
        work_type: workLogForm.work_type,
      });
      toast.success("Work log recorded");
      setWorkLogTarget(null);
      setWorkLogForm(initialWorkLogForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record work log");
    } finally {
      setSaving(false);
    }
  };

  const activePerformers = performers.filter((p) => p.is_active);
  const removedPerformers = performers.filter((p) => !p.is_active);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Performed By</strong>
        {!readOnly && <Button size="sm" variant="outline-primary" onClick={() => setShowAdd(true)}>+ Add Performer</Button>}
      </div>

      {loading ? <div className="text-muted small">Loading...</div> : (
        <Table size="sm" responsive>
          <thead>
            <tr><th>Employee</th><th>Role</th><th>Responsibility</th><th>Contribution</th><th>Effort (est/act)</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {activePerformers.map((p) => (
              <tr key={p.id}>
                <td>{p.user_name}</td>
                <td><Badge bg="light" text="dark">{p.performer_role}</Badge></td>
                <td className="small">{p.responsibility || "—"}</td>
                <td className="small">{p.contribution_percentage != null ? `${p.contribution_percentage}%` : "—"}</td>
                <td className="small">{p.estimated_effort_minutes ?? "—"} / {p.actual_effort_minutes ?? "—"} min</td>
                <td>
                  {readOnly ? <Badge bg="secondary">{p.status}</Badge> : (
                    <Form.Select size="sm" value={p.status} onChange={(e) => changeStatus(p, e.target.value)}>
                      {PERFORMER_STATUSES.filter((s) => s !== "REMOVED").map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  )}
                </td>
                <td>
                  {!readOnly && (
                    <div className="d-flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(p)}>Edit</Button>
                      <Button size="sm" variant="outline-info" onClick={() => setWorkLogTarget(p)}>+ Work</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removePerformer(p)}>Remove</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!activePerformers.length && <tr><td colSpan="7" className="text-muted small">No active performers on this task.</td></tr>}
          </tbody>
        </Table>
      )}

      {removedPerformers.length > 0 && (
        <div className="small text-muted mb-3">
          Previously removed: {removedPerformers.map((p) => p.user_name).join(", ")}
        </div>
      )}

      {workLogs.length > 0 && (
        <>
          <strong className="small d-block mb-2">Performer Work Log</strong>
          <Table size="sm" responsive>
            <thead><tr><th>Date</th><th>Employee</th><th>Type</th><th>Effort</th><th>Description</th><th>Status</th></tr></thead>
            <tbody>
              {workLogs.map((w) => (
                <tr key={w.id}>
                  <td className="small">{w.work_date}</td>
                  <td className="small">{w.user_name}</td>
                  <td><Badge bg="light" text="dark">{w.work_type}</Badge></td>
                  <td className="small">{w.effort_minutes}m</td>
                  <td className="small">{w.work_description || "—"}</td>
                  <td><Badge bg={w.status === "CANCELLED" ? "secondary" : "success"}>{w.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Add performer modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add Performer</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={12}>
              <Form.Label className="small">Employee</Form.Label>
              <Form.Select value={addForm.user_id} onChange={(e) => setAddForm({ ...addForm, user_id: e.target.value })}>
                <option value="">Select...</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Performer Role</Form.Label>
              <Form.Select value={addForm.performer_role} onChange={(e) => setAddForm({ ...addForm, performer_role: e.target.value })}>
                {PERFORMER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Responsibility (optional)</Form.Label>
              <Form.Control value={addForm.responsibility} onChange={(e) => setAddForm({ ...addForm, responsibility: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small">Contribution % (optional)</Form.Label>
              <Form.Control type="number" min={0} max={100} value={addForm.contribution_percentage} onChange={(e) => setAddForm({ ...addForm, contribution_percentage: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small">Estimated Effort (min, optional)</Form.Label>
              <Form.Control type="number" min={0} value={addForm.estimated_effort_minutes} onChange={(e) => setAddForm({ ...addForm, estimated_effort_minutes: e.target.value })} />
            </Col>
          </Row>
          <div className="text-muted small mt-2">Contribution percentages are not required to total 100.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitAdd}>{saving ? "Adding..." : "Add Performer"}</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit performer modal */}
      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Performer</Modal.Title></Modal.Header>
        {editForm && (
          <Modal.Body>
            <Row className="g-2">
              <Col md={12}>
                <Form.Label className="small">Performer Role</Form.Label>
                <Form.Select value={editForm.performer_role} onChange={(e) => setEditForm({ ...editForm, performer_role: e.target.value })}>
                  {PERFORMER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label className="small">Responsibility</Form.Label>
                <Form.Control value={editForm.responsibility} onChange={(e) => setEditForm({ ...editForm, responsibility: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Contribution %</Form.Label>
                <Form.Control type="number" min={0} max={100} value={editForm.contribution_percentage} onChange={(e) => setEditForm({ ...editForm, contribution_percentage: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Estimated Effort (min)</Form.Label>
                <Form.Control type="number" min={0} value={editForm.estimated_effort_minutes} onChange={(e) => setEditForm({ ...editForm, estimated_effort_minutes: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Actual Effort (min)</Form.Label>
                <Form.Control type="number" min={0} value={editForm.actual_effort_minutes} onChange={(e) => setEditForm({ ...editForm, actual_effort_minutes: e.target.value })} />
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitEdit}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>

      {/* Add work log modal */}
      <Modal show={!!workLogTarget} onHide={() => setWorkLogTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Record Work — {workLogTarget?.user_name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label className="small">Date</Form.Label>
              <Form.Control type="date" value={workLogForm.work_date} onChange={(e) => setWorkLogForm({ ...workLogForm, work_date: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small">Effort (minutes)</Form.Label>
              <Form.Control type="number" min={0} value={workLogForm.effort_minutes} onChange={(e) => setWorkLogForm({ ...workLogForm, effort_minutes: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label className="small">Work Type</Form.Label>
              <Form.Select value={workLogForm.work_type} onChange={(e) => setWorkLogForm({ ...workLogForm, work_type: e.target.value })}>
                {WORK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Description (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} value={workLogForm.work_description} onChange={(e) => setWorkLogForm({ ...workLogForm, work_description: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setWorkLogTarget(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitWorkLog}>{saving ? "Saving..." : "Record Work"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
